// @flow
import React, { useState, useEffect } from 'react';
import Tippy from '@tippyjs/react/headless';
import { followCursor } from 'tippy.js';
import { NumericFormat } from 'react-number-format';
import type {
  NumberFormat as NumberFormatType,
  Fakt,
} from '../../map-interactive-states/react/types';
import { ZNCODE } from '../../map-interactive-states/react/types';
import Map from '../../map-interactive-states/react/Map';

export type Props = {
  fakt: Fakt[],
  format: NumberFormatType,
};

const MapContainer = ({ fakt, format }: Props): React$Element<'div'> => {
  const [hoverState, setHoverState] = useState<Fakt | null | void>();
  const [sortedData, setSortedData] = useState<Fakt[] | null | void>(
    Object.keys(ZNCODE).map((i) => ({
      amount: 0,
      faktregion: { zncode: i, title: ZNCODE[i] },
    }))
  );

  useEffect(() => {
    if (fakt) {
      const sorted = fakt;
      Object.keys(ZNCODE).forEach((code) => {
        const hasAmount = sorted.find((i) => i.faktregion.zncode === code);

        if (!hasAmount) {
          sorted.push({
            amount: 0,
            faktregion: { zncode: code, title: ZNCODE[code] },
          });
        }
      });

      setSortedData(sorted);
    }
  }, [fakt]);

  const handleChange = (location?: $Keys<typeof ZNCODE>) => {
    if (sortedData) {
      const stateToFind =
        location &&
        sortedData.find((i) => i.faktregion.zncode === location.toUpperCase());

      setHoverState(stateToFind);
    }
  };

  const renderValue = () => {
    if (hoverState) {
      if (hoverState.amount === 0) {
        return 'Keine Angabe';
      }
      return (
        <NumericFormat
          value={hoverState.amount}
          decimalSeparator={format.decpoint}
          decimalScale={format.decimals}
          thousandSeparator={format.thousandssep}
          suffix={format.unit && ` ${format.unit}`}
          displayType={'text'}
          fixedDecimalScale={true}
        />
      );
    }
    return undefined;
  };

  return (
    <Tippy
      visible={hoverState !== undefined}
      followCursor={true}
      plugins={[followCursor]}
      render={(attrs) => (
        <div className="map-is-teaser__tooltip" tabIndex="-1" {...attrs}>
          <dl className="map-is-teaser__tooltip-list">
            <dt className="map-is-teaser__tooltip-name">
              {hoverState && hoverState.faktregion.title}
            </dt>
            <dd className="map-is-teaser__tooltip-data">{renderValue()}</dd>
          </dl>
          <div className="map-is-teaser__tooltip-arrow" data-popper-arrow></div>
        </div>
      )}
    >
      <div className="map-is-teaser__map-container">
        <Map
          hoverState={hoverState?.faktregion.zncode}
          activeFakt={fakt}
          onStateChange={handleChange}
        />
      </div>
    </Tippy>
  );
};

export default MapContainer;
